import { useEffect, useState } from 'react';
import { AVATAR_REQUEST_PX, getProfilePhotoUrl } from '../lib/images/profilePhotoUrl';
import { useCachedImageSrc } from '../hooks/useCachedImageSrc';
import { glassCss, glassFaces, ringColor, useHues } from '../lib/map/faceStyle';

// Slots 1 and 2 were accent-900 #331550 and accent-950 #2a0d4a. On the 3D map's dark casing
// with its purple edge, two near-black hues read as an empty token or as a selection rather
// than as a person, so the people hand-off (§6) replaces them. The hash is unchanged, so only
// people already on those two slots move - and they move everywhere at once, list row, radar,
// 2D and 3D, which is the point of identity colour.
const AVATAR_COLORS = [
  'bg-accent-500',
  'bg-[#ec4899]',
  'bg-[#84cc16]',
  'bg-accent-850',
  'bg-accent-400',
  'bg-[#06b6d4]',
  'bg-[#10b981]',
  'bg-[#f59e0b]',
  'bg-[#ef4444]',
  'bg-[#3b82f6]',
];

// The same ten hues as hex, index for index, so a puck on the radar or the map wears the hue its list row wears.
const AVATAR_COLORS_HEX = [
  '#7d3ba8',
  '#ec4899',
  '#84cc16',
  '#7a2bb5',
  '#9a49d2',
  '#06b6d4',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#3b82f6',
];

function avatarColorIndex(displayName) {
  let hash = 0;
  for (let i = 0; i < displayName.length; i++)
    hash = (hash << 5) - hash + displayName.charCodeAt(i);
  return Math.abs(hash) % AVATAR_COLORS.length;
}

function getAvatarColor(displayName) {
  if (!displayName) return 'bg-gray-400';
  return AVATAR_COLORS[avatarColorIndex(displayName)];
}

/** Identity hue as a hex string (pucks, sprites). Unknown names get a neutral grey, like the class fallback. */
function getAvatarColorHex(displayName) {
  if (!displayName) return '#9ca3af';
  return AVATAR_COLORS_HEX[avatarColorIndex(displayName)];
}

const SIZE_CLASSES = {
  xxs: 'w-6 h-6 text-[10px]',
  xs: 'w-9 h-9 text-xs',
  sm: 'w-10 h-10 text-sm',
  md: 'w-12 h-12 text-base',
  lg: 'w-24 h-24 text-3xl',
  xl: 'w-32 h-32 text-4xl',
};

const SIZE_PX = {
  xxs: 24,
  xs: 36,
  sm: 40,
  md: 48,
  lg: 96,
  xl: 128,
};

function AvatarInitials({ displayName, sizeClass, className }) {
  const initial = displayName && displayName.charAt(0) ? displayName.charAt(0).toUpperCase() : '?';
  if (glassFaces())
    return (
      <div
        style={glassCss(displayName)}
        className={`rounded-full flex items-center justify-center font-semibold shrink-0 ${sizeClass} ${className}`}
      >
        {initial}
      </div>
    );
  return (
    <div
      className={`rounded-full flex items-center justify-center text-ink font-medium shrink-0 ${getAvatarColor(displayName)} ${sizeClass} ${className}`}
    >
      {initial}
    </div>
  );
}

/**
 * Avatar: profile photo or initial letter circle.
 * On load failure, falls back to initials — never the broken-image glyph.
 */
export default function Avatar({
  displayName,
  profilePhotoUrl,
  size = 'sm',
  className = '',
  loading = 'lazy',
}) {
  useHues(); // a colour someone picked (Edit Profile) redraws their ring
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const layoutPx = SIZE_PX[size] || SIZE_PX.sm;
  const [imageFailed, setImageFailed] = useState(false);
  const url = profilePhotoUrl
    ? getProfilePhotoUrl(profilePhotoUrl, AVATAR_REQUEST_PX[size] || AVATAR_REQUEST_PX.sm)
    : null;
  // Served from the image store once seen, so faces stay with no service (lib/images/imageCache.js).
  const { src, onLoad, retryKey } = useCachedImageSrc(url);

  useEffect(() => {
    setImageFailed(false);
  }, [src, retryKey]);

  if (!src || imageFailed) {
    return <AvatarInitials displayName={displayName} sizeClass={sizeClass} className={className} />;
  }

  return (
    <img
      key={retryKey}
      onLoad={onLoad}
      src={src}
      alt={displayName || 'Avatar'}
      width={layoutPx}
      height={layoutPx}
      loading={loading}
      onError={() => setImageFailed(true)}
      style={
        glassFaces()
          ? { outline: `2px solid ${ringColor(displayName)}`, outlineOffset: '-2px' }
          : undefined
      }
      className={`rounded-full object-cover shrink-0 ${sizeClass} ${className}`}
    />
  );
}

export { getAvatarColor, getAvatarColorHex, getProfilePhotoUrl };
